import BannerComp from "../components/BannerComp";
import Seo from "../components/Seo";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { createBreadcrumbSchema, createWebPageSchema } from "../lib/seo";
import styles from "./PrivacyPolicy.module.css";

const sections = [
  {
    title: "Scope",
    content:
      "This Privacy Policy covers all personal information that PSP collects or processes. It applies to healthcare providers and organizations using our services, employees and contractors working with or on behalf of our clients, and visitors to our website at psprcm.us.",
  },
  {
    title: "SMS Consent",
    content:
      "When you choose SMS updates from PSP, your consent information stays private. We do not share text messaging opt-in data or consent with unrelated third parties for marketing. We may share SMS opt-in status and relevant personal data with trusted service providers, platform vendors, and mobile carriers involved in message delivery.",
  },
  {
    title: "Types of Personal Data We Collect",
    list: [
      "Contact information such as name, email, phone number, and address.",
      "Professional information including licenses, credentials, education, and experience.",
      "Employment information such as role, employer, and work history.",
      "Service data including billing records, user activity, and appointment-related information.",
      "Financial information such as billing contacts and payment details used for invoicing.",
    ],
  },
  {
    title: "How We Collect Personal Data",
    list: [
      "Website forms including contact forms, sign-ups, and service requests.",
      "Direct communication through email, phone calls, and video meetings.",
      "Client onboarding during service setup and business agreements.",
      "Job applications submitted through resumes, CVs, and interviews.",
    ],
  },
  {
    title: "How We Use Personal Data",
    list: [
      "To deliver and manage healthcare administrative services.",
      "To communicate with clients and partners regarding service updates.",
      "To process billing and service-related payments.",
      "To maintain internal business records.",
      "To recruit and onboard employees or contractors.",
      "To improve the quality and security of our services.",
    ],
  },
  {
    title: "Disclosure of Personal Data",
    list: [
      "No mobile opt-in data will be shared with third parties for marketing.",
      "We may disclose information when required for legal compliance.",
      "We work with trusted service providers such as payment processors and software platforms to support operations.",
      "We do not sell, rent, or share personal information for unrelated marketing purposes.",
    ],
  },
  {
    title: "Opt-Out of SMS",
    content:
      "You can stop SMS messages at any time by replying STOP or UNSUBSCRIBE to any message. You can also text your request to (615) 553-6787, and we will remove your number as quickly as possible.",
  },
  {
    title: "Security Measures",
    list: [
      "Encryption of sensitive data.",
      "Access restrictions based on role and responsibility.",
      "Secure databases and cloud infrastructure.",
      "Routine data backups and disaster recovery planning.",
    ],
  },
  {
    title: "Data Retention",
    content:
      "We keep personal information only as long as necessary for operations, legal requirements, or compliance obligations. When it is no longer needed, we securely delete or anonymize it.",
  },
  {
    title: "Your Rights",
    list: [
      "Request access to a copy of your personal data.",
      "Request correction or updates to inaccurate information.",
      "Ask for deletion of personal information where legally permitted.",
      "Object to the processing of your personal data.",
      "File a complaint with a data protection authority when applicable.",
    ],
    footer:
      "To exercise any of these rights, please contact us using the details below.",
  },
  {
    title: "Changes to This Privacy Policy",
    content:
      "We may update this policy to reflect legal requirements, operational changes, or improvements in the way we deliver services. The latest version will always be available on our website. By continuing to use our services, you accept the updated terms.",
  },
];

const seo = {
  title: "Privacy Policy | PSP RCM",
  description:
    "Read the PSP RCM privacy policy to understand how information is collected, handled, and protected.",
  keywords:
    "privacy policy, PSP RCM privacy, healthcare website privacy policy",
  schema: [
    createWebPageSchema({
      path: "/privacy-policy",
      title: "Privacy Policy",
      description:
        "Read the PSP RCM privacy policy to understand how information is collected, handled, and protected.",
    }),
    createBreadcrumbSchema("/privacy-policy", "Privacy Policy"),
  ],
};

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/privacy-policy"
        schema={seo.schema}
      />
      <BannerComp
        heading="Privacy Policy"
        paragraph="We believe transparency is part of trust. This page explains what information PSP collects, how we use it, and the steps we take to protect it."
        BannerBgImg="/banner-bg.webp"
        centered={true}
        form={false}
      />

      <section className={styles.page}>
        <div className={`container ${styles.shell}`}>
          <div className={styles.intro}>
            <p>
              PSP provides healthcare support services for U.S. healthcare
              practices, including medical billing, virtual scribe support,
              credentialing, and back-office services. Protecting private and
              personal information is essential for our clients, employees, and
              visitors, and this policy outlines the standards we follow.
            </p>
          </div>

          <div className={styles.policyContent}>
            {sections.map((section) => (
              <section key={section.title} className={styles.policySection}>
                <h2>{section.title}</h2>
                {section.content ? <p>{section.content}</p> : null}
                {section.list ? (
                  <ul className={styles.list}>
                    {section.list.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
                {section.footer ? <p>{section.footer}</p> : null}
              </section>
            ))}

            <section className={styles.policySection}>
              <h2>Contact Us</h2>
              <div className={styles.contactLinks}>
                <a href="tel:+16155536787" className={styles.contactItem}>
                  <FaPhoneAlt />
                  <span>(615) 553-6787</span>
                </a>
                <a href="mailto:info@psprcm.us" className={styles.contactItem}>
                  <FaEnvelope />
                  <span>info@psprcm.us</span>
                </a>
              </div>
            </section>
          </div>
        </div>
      </section>
    </>
  );
}
